import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import baseModule from "@/store/base.module";
import authModule from "@/store/auth.module";
import VueMeta from "vue-meta";
import {
  MESSAGE_TYPE_INBOX,
  MESSAGE_TYPE_NOTIFICATIONS,
  MESSAGE_TYPE_SENT,
} from "@/config/const";

Vue.use(VueRouter);
Vue.use(VueMeta);

export const routes: Array<RouteConfig> = [
  {
    path: "/auth",
    name: "SignIn",
    component: () => import("../views/auth/SignIn.vue"),
    meta: {
      hideAppHeader: true,
      hideAppNavigation: true,
      onlyWhenLoggedOut: true,
    },
  },
  {
    path: "/sign-up",
    name: "SignUp",
    component: () => import("../views/auth/SignUp.vue"),
    meta: {
      hideAppHeader: true,
      hideAppNavigation: true,
      onlyWhenLoggedOut: true,
    },
  },
  {
    path: "/exec-productions",
    name: "DebtContainer",
    component: () => import("../views/DebtContainer.vue"),
    meta: {
      hideInNavigation: true,
    },
  },
  {
    path: "/mail",
    name: "Mail",
    component: () => import("../views/mail/Mail.vue"),
    redirect: "/notifications",
    meta: {
      hideInNavigation: true,
    },
    children: [
      {
        path: "create",
        name: "Create",
        component: () => import("../views/mail/Create.vue"),
      },
    ],
  },
  {
    path: "/notifications",
    name: "Notifications",
    component: () => import("../views/notifications/MessagesList.vue"),
    meta: {
      hideInNavigation: true,
    },
    props: {
      type: MESSAGE_TYPE_NOTIFICATIONS,
    },
  },
  {
    path: "/notifications/:guid/:id",
    name: "NotificationDetail",
    component: () => import("../views/notifications/MessageDetail.vue"),
    meta: {
      hideInNavigation: true,
    },
    props: {
      isNotification: true,
      type: MESSAGE_TYPE_NOTIFICATIONS,
    },
  },
  // {
  //   path: "/statements",
  //   name: "Statements",
  //   component: () => import("../views/statement/Statements.vue"),
  //   meta: {
  //     hideInNavigation: true,
  //   },
  // },
  {
    path: "/statements/:id/:guid",
    name: "StatementForm",
    component: () => import("../views/statement/StatementForm.vue"),
    meta: {
      hideInNavigation: true,
    },
    props: true,
  },
  {
    path: "/requests",
    name: "Requests",
    component: () => import("../components/employer/Requests.vue"),
    meta: {
      hideInNavigation: true,
    },
    props: true,
  },
  {
    path: "/employees-with-penalty",
    name: "EmployeesWithPenalty",
    component: () => import("../components/employer/EmployeesWithPenalty.vue"),
    meta: {
      hideInNavigation: true,
    },
    props: true,
  },
  {
    path: "/employee-details",
    name: "EmployeeDetails",
    component: () => import("../components/employer/EmployeeDetails.vue"),
    meta: {
      hideInNavigation: true,
    },
    props: true,
  },
  {
    path: "/wage-orders",
    name: "WageOrders",
    component: () => import("../components/employer/WageOrders.vue"),
    meta: {
      hideInNavigation: true,
    },
    props: true,
  },
  {
    path: "/claimant-analytics",
    name: "ClaimantAnalytics",
    component: () =>
      import("../components/claimant/AnalyticsDashboards.vue" as any),
    meta: {
      hideInNavigation: true,
    },
    props: true,
  },
  {
    path: "/claimant-productions",
    name: "ClaimantProductions",
    component: () =>
      import("../components/claimant/ExecProductions.vue" as any),
    meta: {
      hideInNavigation: true,
    },
    props: true,
  },
  {
    path: "/claimant-arrests",
    name: "ClaimantArrests",
    component: () => import("../components/claimant/ArrestList.vue" as any),
    meta: {
      hideInNavigation: true,
    },
    props: true,
  },
  {
    path: "/claimant-monitoring",
    name: "ClaimantMonitoring",
    component: () =>
      import("../components/claimant/MonitoringProductions.vue" as any),
    meta: {
      hideInNavigation: true,
    },
    props: true,
  },
  {
    path: "/resubmissions",
    name: "Resubmissions",
    component: () =>
      import("../components/resubmissions/ResubmissionsMainView.vue" as any),
    meta: {
      hideInNavigation: true,
    },
  },
  {
    path: "/resubmissions/search",
    name: "ResubmissonSearch",
    component: () =>
      import("../components/resubmissions/SearchView.vue" as any),
    meta: {
      hideInNavigation: true,
    },
  },
  {
    path: "/resubmissions/:id",
    name: "ResubmissionForm",
    component: () =>
      import("../components/resubmissions/ResubmissionForm.vue" as any),
    meta: {
      hideInNavigation: true,
    },
    props: true,
  },
  {
    path: "/bank-details",
    name: "BankDetails",
    component: () =>
      import("../components/bank-details/BankDetailsMainView.vue" as any),
    meta: {
      hideInNavigation: true,
    },
  },
  {
    path: "/citizen-applications",
    name: "CitizenApplications",
    component: () =>
      import("../views/citizen-application/CitizenApplications.vue" as any),
    meta: {
      hideInNavigation: true,
    },
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  function getMeta(key: string) {
    return to.matched.find((record) => record.meta[key])?.meta[key];
  }

  if (!authModule.loggedIn && !getMeta("onlyWhenLoggedOut"))
    return authModule.redirectToWSOLoginPage();

  if (authModule.isEmployerNotActive) {
    return await authModule.logout();
  }

  if (!to.name) return next(baseModule.defaultRoute);

  baseModule.setShowHeader(!getMeta("hideAppHeader"));
  baseModule.setShowNavigation(!getMeta("hideAppNavigation"));

  next();
});

export default router;
